import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import {
  useIsAuthenticated,
  useMsalAuthentication,
  useMsal,
  useAccount,
} from '@azure/msal-react';
import {
  AccountInfo,
  InteractionType,
  InteractionStatus,
  AuthenticationResult,
} from '@azure/msal-browser';
import api from 'configs/api';

interface IAuthContextData {
  isAuthenticated: boolean;
  signOut: () => void;
  account: AccountInfo | null | undefined;
  // eslint-disable-next-line no-unused-vars
  checkPermissions(permissions: string[]): boolean;
}

const AuthContext = React.createContext<IAuthContextData>(
  {} as IAuthContextData,
);

/**
 * Caso tenha problemas com o token esses links podem ser úteis:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-auth-code-flow#refresh-the-access-token
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-react-samples
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/token-lifetimes.md#token-renewal
 * https://stackoverflow.com/questions/64407656/msal-js-v2-3-acquiretokensilent-returning-empty-access-token
 * https://thedutchlab.com/blog/using-axios-interceptors-for-refreshing-your-api-token
 * https://github.com/axios/axios/issues/934
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/token-lifetimes.md
 */

const AuthProvider: React.FC = ({ children }) => {
  useMsalAuthentication(InteractionType.Redirect);

  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [atsResponse, setAtsResponse] = useState<AuthenticationResult>();

  const roles = useMemo(() => {
    let userRoles: string[] = [];
    if (account) {
      userRoles = (account?.idTokenClaims as { roles: string[] }).roles || [];
    }
    return userRoles;
  }, [account]);

  const checkPermissions = React.useCallback(
    (permissions: string[]): boolean => {
      return permissions.some(permission =>
        roles
          .map(role => role.toLowerCase())
          .includes(permission.toLowerCase()),
      );
    },
    [roles],
  );

  const acquireToken = React.useCallback(
    async (acc: AccountInfo, forceRefresh = false) => {
      const request = {
        scopes: ['User.Read'],
        account: acc,
        forceRefresh,
      };
      try {
        const response = await instance.acquireTokenSilent(request);
        if (!response.idToken) instance.acquireTokenRedirect(request);
        return response;
      } catch (e) {
        instance.acquireTokenRedirect(request);
      }
      return null;
    },
    [instance],
  );

  useEffect(() => {
    if (!atsResponse && account && inProgress === InteractionStatus.None) {
      acquireToken(account).then(response => {
        if (response) {
          setAtsResponse(response);
        }
      });
    }
  }, [account, acquireToken, atsResponse, inProgress]);

  const signOut = useCallback(() => instance.logout(), [instance]);

  useEffect(() => {
    api.interceptors.request.use(
      async config => {
        if (account) {
          const response = await acquireToken(account);
          if (response) {
            return {
              ...config,
              headers: {
                Authorization: `${response.tokenType} ${response.idToken}`,
                'Content-Type': 'application/json',
              },
            };
          }
        }
        return config;
      },
      error => Promise.reject(error),
    );

    api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        if (error.response.status === 401) {
          if (account) {
            const response = await acquireToken(account, true);
            if (response) {
              setAtsResponse(response);
              return api({
                ...originalRequest,
                headers: {
                  Authorization: `${response.tokenType} ${response.idToken}`,
                  'Content-Type': 'application/json',
                },
              });
            }
          }
        }
        return Promise.reject(error);
      },
    );
  }, [account, acquireToken]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, signOut, account, checkPermissions }}
    >
      {atsResponse ? children : null}
    </AuthContext.Provider>
  );
};

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}
export { AuthProvider, useAuth, AuthContext };
